import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles/components/NotificationsPanel.scss';
import { UserContext } from '../UserContext';
import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    getDoc,
    writeBatch,
    limit,
    startAfter,
    runTransaction,
    increment,
    serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebasedeconfig';
import notificationService from '../services/notificationService';
import VideoCallManager from './VideoCallManager';
import { useLanguage } from '../contexts/LanguageContext';
import ActivityGainToast from './ActivityGainToast';
import { XP_WEIGHTS } from './levelUtils';

// Importation des ressources pour les signes du zodiaque
const zodiacIcons = {
    "aquarius": "https://zupimages.net/up/24/40/tl6v.png",   // Verseau ok
    "aries": "https://zupimages.net/up/24/40/b9ni.png",      // Bélier ok
    "capricorn": "https://zupimages.net/up/24/40/ymmj.png",  // Capricorne ok
    "cancer": "https://zupimages.net/up/24/40/wd4o.png",     // Cancer ok
    "gemini": "https://zupimages.net/up/24/40/tbk8.png",     // Gémeaux ok
    "leo": "https://zupimages.net/up/24/40/kwjb.png",        // Lion ok
    "libra": "https://zupimages.net/up/24/40/kow9.png",      // Balance ok
    "pisces": "https://zupimages.net/up/24/40/oza3.png",     // Poissons ok
    "sagittarius": "https://zupimages.net/up/24/40/d9jv.png",// Sagittaire ok
    "scorpio": "https://zupimages.net/up/24/40/codd.png",    // Scorpion ok
    "taurus": "https://zupimages.net/up/24/40/tuvp.png",     // Taureau ok
    "virgo": "https://zupimages.net/up/24/40/j3zu.png"       // Vierge ok
};

const getSeasonalBonusConfig = (notification, userData = {}) => {
    if (!notification) return null;

    const year = new Date().getFullYear();

    if (notification.type === 'zodiac') {
        const zodiacSign = notification.zodiacSign || userData.zodiacSign;

        if (!zodiacSign) return null;

        return {
            year,
            claimKey: `zodiac_${zodiacSign}`,
            points: 5,
            source: 'zodiac',
        };
    }

    if (notification.type !== 'holiday') {
        return null;
    }

    const holidayRules = {
        "le Noël": {
            claimKey: "holiday_christmas",
            points: 3,
        },
        "l'Halloween": {
            claimKey: "holiday_halloween",
            points: 3,
        },
        "la Pâques": {
            claimKey: "holiday_easter",
            points: 3,
        },
        "la Saint-Valentin": {
            claimKey: "holiday_valentine",
            points: 3,
        },
        "le Nouvel An": {
            claimKey: "holiday_new_year",
            points: 3,
        },
        "la Fête du Canada": {
            claimKey: "holiday_canada_day",
            points: 5,
            country: "CA",
        },
        "la Fête Nationale (France)": {
            claimKey: "holiday_france_day",
            points: 5,
            country: "FR",
        },
        "la Fête Nationale (États-Unis)": {
            claimKey: "holiday_usa_day",
            points: 5,
            country: "US",
        },
        "la Saint-Jean-Baptiste": {
            claimKey: "holiday_st_jean_baptiste",
            points: 10,
            country: "CA",
            state: "QC",
        },
    };

    const holidayName =
        notification.holidayName ||
        notification.holiday ||
        notification.currentHoliday ||
        "";

    const rule = holidayRules[holidayName];

    if (!rule) return null;

    const userCountry = String(userData.country || "").trim().toUpperCase();
    const userState = String(userData.state || "").trim().toUpperCase();

    if (rule.country && userCountry !== rule.country) {
        return null;
    }

    if (rule.state && userState !== rule.state) {
        return null;
    }

    return {
        year,
        claimKey: rule.claimKey,
        points: rule.points,
        source: 'holiday',
    };
};

const toDateSafe = (value) => {
    if (!value) return null;

    if (typeof value.toDate === "function") {
        return value.toDate();
    }

    if (value instanceof Date) {
        return value;
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }

    return parsedDate;
};

const getFullYearsSince = (startDate, now = new Date()) => {
    if (!startDate) return 0;

    let years = now.getFullYear() - startDate.getFullYear();

    const hasNotReachedAnniversaryYet =
        now.getMonth() < startDate.getMonth() ||
        (now.getMonth() === startDate.getMonth() && now.getDate() < startDate.getDate());

    if (hasNotReachedAnniversaryYet) {
        years -= 1;
    }

    return Math.max(years, 0);
};

const getLoyaltyBonusConfig = (notification, userData = {}) => {
    if (!notification || notification.type !== "loyalty") {
        return null;
    }

    const registeredDate = toDateSafe(userData.registeredAt || userData.createdAt);

    if (!registeredDate) {
        return null;
    }

    const milestoneYears = Number(notification.milestoneYears || notification.years || 0);

    const loyaltyRules = {
        5: {
            claimKey: "years_5",
            points: 50,
        },
        10: {
            claimKey: "years_10",
            points: 100,
        },
    };

    const rule = loyaltyRules[milestoneYears];

    if (!rule) {
        return null;
    }

    const accountAgeYears = getFullYearsSince(registeredDate);

    if (accountAgeYears < milestoneYears) {
        return null;
    }

    return {
        claimKey: rule.claimKey,
        points: rule.points,
        milestoneYears,
    };
};

const formatActivityGainPoints = (value) => {
    const number = Number(value) || 0;

    return `+${Number.isInteger(number)
        ? number
        : number.toFixed(2).replace(/\.?0+$/, "")
        }`;
};
const NotificationsPanel = ({ isOpen, onClose }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    const { userId, isLoggedIn } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const { t, currentLanguage } = useLanguage();
    const [activityGainToast, setActivityGainToast] = useState(null);

    // États pour l'appel vidéo depuis les notifications
    const [showVideoCall, setShowVideoCall] = useState(false);
    const [videoCallTarget, setVideoCallTarget] = useState(null);

    // Fonction pour traduire le contenu des notifications
    const translateNotificationContent = useCallback((notification) => {
        if (!notification) return notification;

        const translated = { ...notification };

        // Traduire le message selon le type
        switch (notification.type) {
            case 'message':
                translated.message = t('notifications.newMessage')
                    .replace('{{username}}', `<strong style="color: #00FFFF">${notification.fromUsername || t('forum.userUnknown')}</strong>`);
                break;
            case 'call':
            case 'call_incoming':
                translated.message = t('notifications.incomingCall')
                    .replace('{{username}}', `<strong style="color: #00FFFF">${notification.fromUsername || t('forum.userUnknown')}</strong>`);
                break;
            case 'call_missed':
                translated.message = t('notifications.missedCall')
                    .replace('{{username}}', `<strong style="color: #00FFFF">${notification.fromUsername || t('forum.userUnknown')}</strong>`);
                break;
            case 'call_ended':
                translated.message = t('notifications.callEnded')
                    .replace('{{username}}', `<strong style="color: #00FFFF">${notification.fromUsername || t('forum.userUnknown')}</strong>`);
                break;
            case 'call_callback':
                translated.message = t('notifications.callbackRequest')
                    .replace('{{username}}', `<strong style="color: #00FFFF">${notification.fromUsername || t('forum.userUnknown')}</strong>`);
                break;
            case 'forum_hot':
                translated.message = t('notifications.forumHot')
                    .replace('{{username}}', `<strong style="color: #00FFFF">${notification.fromUsername || t('forum.userUnknown')}</strong>`)
                    .replace('{{subject}}', notification.subjectTitle || t('common.topic'));
                break;
            case 'forum_cold':
                translated.message = t('notifications.forumCold')
                    .replace('{{username}}', `<strong style="color: #00FFFF">${notification.fromUsername || t('forum.userUnknown')}</strong>`)
                    .replace('{{subject}}', notification.subjectTitle || t('common.topic'));
                break;
            case 'forum_reply':
                translated.message = t('notifications.forumReply')
                    .replace('{{username}}', `<strong style="color: #00FFFF">${notification.fromUsername || t('forum.userUnknown')}</strong>`)
                    .replace('{{subject}}', notification.subjectTitle || t('common.topic'));
                break;
            case 'forum_like':
                translated.message = t('notifications.forumLike')
                    .replace('{{username}}', `<strong style="color: #00FFFF">${notification.fromUsername || t('forum.userUnknown')}</strong>`)
                    .replace('{{subject}}', notification.subjectTitle || t('common.topic'));
                break;
            case 'zodiac':
                const zodiacTranslated = t(`zodiac.${notification.zodiacSign}`, notification.zodiacSign);
                translated.message = t('notifications.zodiacPeriod')
                    .replace('{{zodiacSign}}', zodiacTranslated);
                break;
            case 'holiday': {
                const holidayName =
                    notification.holidayName ||
                    notification.holiday ||
                    notification.currentHoliday ||
                    "";

                const holidayTranslated = t(`holidays.${holidayName}`, holidayName);

                translated.message = t('notifications.holidayGreeting')
                    .replace('{{holiday}}', holidayTranslated);

                break;
            }
            case 'loyalty': {
                const milestoneYears = notification.milestoneYears || notification.years || "";

                translated.message = t(
                    'notifications.loyaltyBonus',
                    'Merci pour vos {{years}} ans sur MovieFrenzy !'
                ).replace('{{years}}', milestoneYears);

                break;
            }
            case 'level_up':
                const levelUpTitle = t(
                    `forum.levelTitles.${notification.levelKey}`,
                    notification.levelTitle || notification.levelKey
                );

                translated.message = t('notifications.levelUp')
                    .replace(
                        '{{levelTitle}}',
                        `<strong style="color: #D90093">${levelUpTitle}</strong>`
                    );
                break;
            default:
                // Garder le message original si pas de traduction
                break;
        }

        return translated;
    }, [t]);

    // 🔧 NOUVEAU : Effet pour nettoyer lors de la déconnexion
    useEffect(() => {
        if (!isLoggedIn) {
            setNotifications([]);
            setUnreadCount(0);
            setLastDoc(null);
            setHasMore(false);
            setLoading(false);
            setError(null);
        }
    }, [isLoggedIn]);

    // Initialisation et écoute des notifications
    useEffect(() => {
        if (!userId || !isOpen) {
            // 🔧 NOUVEAU : Réinitialiser les notifications si pas d'utilisateur
            if (!userId) {
                setNotifications([]);
                setUnreadCount(0);
                setLastDoc(null);
                setHasMore(false);
            }
            return;
        }

        setLoading(true);
        setError(null);

        const notificationsRef = collection(db, 'users', userId, 'notifications');
        const firstQuery = query(
            notificationsRef,
            orderBy('createdAt', 'desc'),
            limit(20)
        );

        // Écoute en temps réel
        const unsubscribe = onSnapshot(firstQuery,
            (snapshot) => {
                try {
                    const list = snapshot.docs.map(d => {
                        const data = { id: d.id, ...d.data() };
                        return translateNotificationContent(data);
                    });
                    setNotifications(list);
                    setLoading(false);

                    // Compter les non lues
                    const unread = list.filter(notif => !notif.read).length;
                    setUnreadCount(unread);

                    const lastVisible = snapshot.docs[snapshot.docs.length - 1];
                    setLastDoc(lastVisible || null);
                    setHasMore(snapshot.size === 20);
                } catch (err) {
                    console.error("Erreur traitement notifications:", err);
                    setError(t('notifications.errorProcessing'));
                    setLoading(false);
                }
            },
            (error) => {
                console.error("Erreur lors de la récupération des notifications:", error);
                setError(t('notifications.errorConnection'));
                setLoading(false);
            }
        );

        // Marquer toutes comme lues après un délai
        const markAllAsReadTimer = setTimeout(() => {
            if (isOpen && unreadCount > 0) {
                markAllAsRead();
            }
        }, 2000);

        return () => {
            unsubscribe();
            clearTimeout(markAllAsReadTimer);
        };
    }, [userId, isOpen, currentLanguage, translateNotificationContent]);

    // Marquer toutes les notifications comme lues
    const markAllAsRead = useCallback(async () => {
        if (!userId) return;
        try {
            await notificationService.markAllAsRead(userId);
            setUnreadCount(0);
        } catch (error) {
            console.error("Erreur lors du marquage des notifications comme lues:", error);
        }
    }, [userId]);

    // Lancer un appel vidéo depuis une notification
    const handleVideoCallFromNotification = useCallback(async (notification, event) => {
        event.stopPropagation();

        if (!notification.callData) return;

        try {
            // Récupérer les données complètes de l'utilisateur
            const userDocRef = doc(db, 'users', notification.fromUserId);
            const userDoc = await getDoc(userDocRef);
            const userData = userDoc.exists() ? userDoc.data() : {};

            const targetUser = {
                uid: notification.fromUserId,
                userId: notification.fromUserId,
                username: notification.fromUsername || userData.username,
                avatar: userData.avatar || notification.fromAvatar,
                displayName: userData.username || notification.fromUsername,
            };

            setVideoCallTarget(targetUser);
            setShowVideoCall(true);

            // Marquer la notification comme lue
            if (!notification.read) {
                await notificationService.markAsRead(userId, notification.id);
            }

        } catch (error) {
            console.error("Erreur lors du lancement de l'appel vidéo:", error);
            setError(t('notifications.errorOpening'));
        }
    }, [userId]);

    // Fermer l'appel vidéo
    const handleCloseVideoCall = useCallback(() => {
        setShowVideoCall(false);
        setVideoCallTarget(null);
    }, []);

    // Scroll helper avec tentatives multiples (cas où la section se monte légèrement plus tard)
    const scrollToSection = useCallback((selector, attempts = 40, delay = 250) => {
        const scrollElementWithOffset = () => {
            const sectionElement = document.querySelector(selector);
            if (!sectionElement) return false;

            const nav = document.querySelector('.navigation');
            const navHeight = nav ? nav.getBoundingClientRect().height : 0;
            const extraOffset = window.innerWidth <= 992 ? 150 : 60;
            const offset = navHeight + extraOffset;
            const targetTop = sectionElement.getBoundingClientRect().top + window.pageYOffset - offset;

            window.scrollTo({
                top: Math.max(targetTop, 0),
                behavior: 'smooth'
            });

            const followUps = window.innerWidth <= 992 ? 3 : 1;
            for (let i = 1; i <= followUps; i++) {
                setTimeout(() => {
                    const el = document.querySelector(selector);
                    if (!el) return;
                    const newTop = el.getBoundingClientRect().top + window.pageYOffset - offset;
                    window.scrollTo({
                        top: Math.max(newTop, 0),
                        behavior: 'smooth'
                    });
                }, i * 500);
            }

            return true;
        };

        if (scrollElementWithOffset()) {
            return;
        }

        if (attempts > 0) {
            setTimeout(() => scrollToSection(selector, attempts - 1, delay), delay);
        }
    }, []);

    const navigateHomeToSection = useCallback(() => {
        const selector = '.section-3_default';
        const isOnHome = location.pathname === '/' || location.pathname === '';

        if (typeof window !== 'undefined' && window.sessionStorage) {
            try {
                window.sessionStorage.setItem('mf-scroll-target', selector);
            } catch (error) {
                console.warn('sessionStorage indisponible pour le scroll ciblé', error);
            }
        }

        if (isOnHome) {
            scrollToSection(selector);
            setTimeout(() => {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    try {
                        window.sessionStorage.removeItem('mf-scroll-target');
                    } catch { /* noop */ }
                }
            }, 1000);
            return;
        }

        navigate('/', { state: { scrollTo: selector } });
        scrollToSection(selector);
    }, [location.pathname, navigate, scrollToSection]);

    const claimSeasonalBonus = useCallback(async (notification) => {
        if (!userId || !notification) return null;

        const userRef = doc(db, "users", userId);

        try {
            return await runTransaction(db, async (transaction) => {
                const userSnap = await transaction.get(userRef);

                if (!userSnap.exists()) {
                    return null;
                }

                const userData = userSnap.data();
                const bonusConfig = getSeasonalBonusConfig(notification, userData);

                if (!bonusConfig || bonusConfig.points <= 0) {
                    return null;
                }

                const alreadyClaimed =
                    userData.xp?.claims?.seasonal?.[bonusConfig.year]?.[bonusConfig.claimKey];

                if (alreadyClaimed) {
                    return {
                        claimed: false,
                        alreadyClaimed: true,
                        points: 0,
                        claimKey: bonusConfig.claimKey,
                    };
                }

                const claimPath = `xp.claims.seasonal.${bonusConfig.year}.${bonusConfig.claimKey}`;

                transaction.update(userRef, {
                    "xp.sources.seasonalBonusPoints": increment(bonusConfig.points),
                    [claimPath]: true,
                    "xp.updatedAt": serverTimestamp(),
                });

                return {
                    claimed: true,
                    alreadyClaimed: false,
                    points: bonusConfig.points,
                    claimKey: bonusConfig.claimKey,
                };
            });
        } catch (error) {
            console.error("Erreur lors de la réclamation du bonus saisonnier:", error);
            return null;
        }
    }, [userId]);

    const claimLoyaltyBonus = useCallback(async (notification) => {
        if (!userId || !notification) return null;

        const userRef = doc(db, "users", userId);

        try {
            return await runTransaction(db, async (transaction) => {
                const userSnap = await transaction.get(userRef);

                if (!userSnap.exists()) {
                    return null;
                }

                const userData = userSnap.data();
                const bonusConfig = getLoyaltyBonusConfig(notification, userData);

                if (!bonusConfig || bonusConfig.points <= 0) {
                    return null;
                }

                const alreadyClaimed =
                    userData.xp?.claims?.loyalty?.[bonusConfig.claimKey];

                if (alreadyClaimed) {
                    return {
                        claimed: false,
                        alreadyClaimed: true,
                        points: 0,
                        claimKey: bonusConfig.claimKey,
                    };
                }

                const claimPath = `xp.claims.loyalty.${bonusConfig.claimKey}`;

                transaction.update(userRef, {
                    "xp.sources.loyaltyBonusPoints": increment(bonusConfig.points),
                    [claimPath]: true,
                    "xp.updatedAt": serverTimestamp(),
                });

                return {
                    claimed: true,
                    alreadyClaimed: false,
                    points: bonusConfig.points,
                    claimKey: bonusConfig.claimKey,
                };
            });
        } catch (error) {
            console.error("Erreur lors de la réclamation du bonus fidélité:", error);
            return null;
        }
    }, [userId]);

    const showLikeReceivedActivityToast = useCallback((notificationId) => {
        const storageKey = `movieFrenzy.likeReceivedToast.${notificationId || Date.now()}`;

        try {
            if (window.sessionStorage.getItem(storageKey)) {
                return;
            }

            window.sessionStorage.setItem(storageKey, "true");
        } catch (error) {
            console.warn("sessionStorage indisponible pour le toast likesReceived", error);
        }

        setActivityGainToast({
            id: `like-received-${notificationId || Date.now()}`,
            points: formatActivityGainPoints(XP_WEIGHTS.likesReceived),
            unitLabel: t("profilMember.activity"),
            label: t("activityToast.likeReceived"),
            variant: "like",
        });
    }, [t]);

    // Gestion du clic sur une notification
    const handleNotificationClick = useCallback(async (notification, event) => {
        try {
            // Marquer comme lue immédiatement
            if (!notification.read) {
                await notificationService.markAsRead(userId, notification.id);
            }

            if (notification.type === 'message') {
                // Récupérer les données complètes de l'utilisateur
                const userDocRef = doc(db, 'users', notification.fromUserId);
                const userDoc = await getDoc(userDocRef);
                const userData = userDoc.exists() ? userDoc.data() : {};

                if (event.target.closest('.notification-username')) {
                    // Ouvrir le profil
                    const profilData = {
                        uid: notification.fromUserId,
                        userId: notification.fromUserId,
                        username: notification.fromUsername || userData.username,
                        avatar: userData.avatar || null,
                        photoURL: userData.avatar || null,
                        displayName: userData.username || notification.fromUsername,
                        occupation: userData.occupation || '',
                        banner: userData.banner || null,
                        biography: userData.biography || '',
                        quote: userData.quote || '',
                        gender: userData.gender || '',
                        age: userData.age || '',
                        zodiacSign: userData.zodiacSign || '',
                        country: userData.country || '',
                        state: userData.state || '',
                        city: userData.city || '',
                        favoritePreviews: userData.favoritePreviews || [],
                        favoriteActors: userData.favoriteActors || [],
                        favoriteCategories: userData.favoriteCategories || [],
                        favoriteMovie: userData.favoriteMovie || null,
                        timestamp: new Date().getTime()
                    };

                    localStorage.setItem('showEmuleProfil', JSON.stringify(profilData));

                    if (window.location.pathname === '/' || window.location.pathname === '') {
                        window.dispatchEvent(new CustomEvent('openEmuleProfil', { detail: profilData }));
                    } else {
                        navigate('/');
                    }
                } else {
                    // Ouvrir le chat
                    const chatData = {
                        userId: notification.fromUserId,
                        uid: notification.fromUserId,
                        username: notification.fromUsername || userData.username,
                        displayName: userData.username || notification.fromUsername,
                        avatar: userData.avatar || null,
                        photoURL: userData.avatar || null,
                        forceOpen: true,
                        timestamp: new Date().getTime()
                    };

                    localStorage.setItem('openChatWith', JSON.stringify(chatData));

                    if (window.location.pathname === '/' || window.location.pathname === '') {
                        window.dispatchEvent(new CustomEvent('openChatWindow', { detail: chatData }));
                    } else {
                        navigate('/');
                    }
                }
            }
            else if (['call', 'call_missed', 'call_ended', 'call_incoming', 'call_callback'].includes(notification.type)) {
                // Pour toutes les notifications d'appels, ouvrir le profil de l'appelant
                const userDocRef = doc(db, 'users', notification.fromUserId);
                const userDoc = await getDoc(userDocRef);
                const userData = userDoc.exists() ? userDoc.data() : {};

                const profilData = {
                    uid: notification.fromUserId,
                    userId: notification.fromUserId,
                    username: notification.fromUsername || userData.username,
                    avatar: userData.avatar || null,
                    photoURL: userData.avatar || null,
                    displayName: userData.username || notification.fromUsername,
                    occupation: userData.occupation || '',
                    banner: userData.banner || null,
                    biography: userData.biography || '',
                    quote: userData.quote || '',
                    gender: userData.gender || '',
                    age: userData.age || '',
                    zodiacSign: userData.zodiacSign || '',
                    country: userData.country || '',
                    state: userData.state || '',
                    city: userData.city || '',
                    favoritePreviews: userData.favoritePreviews || [],
                    favoriteActors: userData.favoriteActors || [],
                    favoriteCategories: userData.favoriteCategories || [],
                    favoriteMovie: userData.favoriteMovie || null,
                    timestamp: new Date().getTime()
                };

                localStorage.setItem('showEmuleProfil', JSON.stringify(profilData));

                if (window.location.pathname === '/' || window.location.pathname === '') {
                    window.dispatchEvent(new CustomEvent('openEmuleProfil', { detail: profilData }));
                } else {
                    navigate('/');
                }
            }
            //Étape 1
            // else if (notification.type === 'level_up') {
            //     const profileUserId = notification.profileUserId || userId;
            //     navigate(`/profilMemberPage/${profileUserId}`);
            // }
            else if (notification.type === 'level_up') {
                const levelUpPayload = {
                    levelKey:
                        notification.levelKey ||
                        notification.lastNotifiedLevelKey ||
                        "aspirant_figuring",

                    levelNumber:
                        notification.levelNumber ||
                        notification.lastNotifiedLevelNumber ||
                        null,

                    createdAt: Date.now(),

                    // Visuel temporaire : 15 minutes
                    expiresAt: Date.now() + 1000 * 60 * 15,
                };

                try {
                    window.sessionStorage.setItem(
                        "mf-level-up-visual",
                        JSON.stringify(levelUpPayload)
                    );
                } catch (error) {
                    console.warn("sessionStorage indisponible pour level_up", error);
                }

                window.dispatchEvent(
                    new CustomEvent("mf-level-up-visual-request", {
                        detail: levelUpPayload,
                    })
                );

                navigateHomeToSection();
            }
            else if (['forum_hot', 'forum_reply', 'forum_like', 'forum_cold'].includes(notification.type)) {
                const targetReplyId =
                    notification.replyId ||
                    notification.targetReplyId ||
                    notification.messageId ||
                    null;

                if (notification.type === "forum_like") {
                    showLikeReceivedActivityToast(notification.id);
                }

                if (notification.type === "forum_like" && targetReplyId) {
                    navigate(`/subject/${notification.subjectId}#reply-${targetReplyId}`, {
                        state: {
                            replyId: targetReplyId,
                            fromForumLikeNotification: true,
                        },
                    });
                } else {
                    navigate(`/subject/${notification.subjectId}`);
                }
            }
            else if (notification.type === 'loyalty') {
                await claimLoyaltyBonus(notification);
                navigate(`/profilMemberPage/${userId}`);
            }
            else if (['zodiac', 'holiday'].includes(notification.type)) {
                await claimSeasonalBonus(notification);
                navigateHomeToSection();
            }

            // Fermer le panneau
            onClose();

        } catch (error) {
            console.error("Erreur lors du traitement de la notification:", error);
            setError(t('notifications.errorOpening'));
        }
    }, [
        userId,
        navigate,
        onClose,
        navigateHomeToSection,
        claimSeasonalBonus,
        claimLoyaltyBonus,
        showLikeReceivedActivityToast,
    ]);

    // Supprimer une notification
    const handleDeleteNotification = useCallback(async (event, notificationId) => {
        event.stopPropagation();

        if (!notificationId || !userId) return;

        try {
            setDeleting(true);
            await notificationService.deleteNotification(userId, notificationId);

            // Mise à jour locale
            setNotifications(prev => prev.filter(notif => notif.id !== notificationId));

        } catch (error) {
            console.error("Erreur lors de la suppression de la notification:", error);
            setError(t('notifications.errorDeleting'));
        } finally {
            setDeleting(false);
        }
    }, [userId]);

    // Supprimer toutes les notifications
    const handleClearAllNotifications = useCallback(async () => {
        if (!userId || notifications.length === 0) return;

        if (!window.confirm(t('notifications.deleteConfirm'))) {
            return;
        }

        try {
            setDeleting(true);

            // Traiter par lots
            const batchSize = 20;
            for (let i = 0; i < notifications.length; i += batchSize) {
                const batch = notifications.slice(i, i + batchSize);
                const deletePromises = batch.map(notification =>
                    notificationService.deleteNotification(userId, notification.id)
                );
                await Promise.all(deletePromises);
            }

            setNotifications([]);
            setUnreadCount(0);

        } catch (error) {
            console.error("Erreur lors de la suppression de toutes les notifications:", error);
            setError(t('notifications.errorDeleting'));
        } finally {
            setDeleting(false);
        }
    }, [userId, notifications]);

    // Charger plus de notifications
    const handleLoadMore = useCallback(async () => {
        if (!userId || !lastDoc || loading) return;

        try {
            setLoading(true);
            const notificationsRef = collection(db, 'users', userId, 'notifications');
            const nextQuery = query(
                notificationsRef,
                orderBy('createdAt', 'desc'),
                startAfter(lastDoc),
                limit(20)
            );

            const snapshot = await getDocs(nextQuery);
            const list = snapshot.docs.map(d => {
                const data = { id: d.id, ...d.data() };
                return translateNotificationContent(data);
            });

            setNotifications(prev => [...prev, ...list]);

            const newLast = snapshot.docs[snapshot.docs.length - 1];
            setLastDoc(newLast || null);
            setHasMore(snapshot.size === 20);

        } catch (err) {
            console.error('Erreur lors du chargement plus:', err);
            setError(t('notifications.errorLoading'));
        } finally {
            setLoading(false);
        }
    }, [userId, lastDoc, loading]);

    // Redirection vers la connexion
    const handleRedirectToLogin = useCallback(() => {
        onClose();
        navigate('/connexion');
    }, [onClose, navigate]);

    // Formater la date
    const formatDate = useCallback((timestamp) => {
        if (!timestamp) return '';

        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('notifications.justNow');
        if (diffMins < 60) return diffMins === 1 ? t('notifications.oneMinuteAgo') : t('notifications.minutesAgo').replace('{{count}}', diffMins);
        if (diffHours < 24) return diffHours === 1 ? t('notifications.oneHourAgo') : t('notifications.hoursAgo').replace('{{count}}', diffHours);
        if (diffDays < 7) return diffDays === 1 ? t('notifications.oneDayAgo') : t('notifications.daysAgo').replace('{{count}}', diffDays);

        const locale = currentLanguage === 'en' ? 'en-US' : currentLanguage === 'es' ? 'es-ES' : currentLanguage === 'de' ? 'de-DE' : 'fr-FR';

        return date.toLocaleDateString(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }, [t, currentLanguage]);

    // Obtenir la classe CSS selon le type
    const getNotificationClass = useCallback((type) => {
        const baseClass = 'notification-item';
        const typeClass = `notification-${type.replace('_', '-')}`;
        return `${baseClass} ${typeClass}`;
    }, []);

    // Obtenir l'icône selon le type (améliorée avec de nouvelles icônes)
    const getNotificationIcon = useCallback((type, notification) => {
        const icons = {
            message: '💬',
            call: '📞',
            call_incoming: '📲',
            call_missed: '📞',
            call_ended: '📱',
            call_callback: '🔄',
            forum_hot: '🔥',
            forum_cold: '❄️',
            forum_reply: '💬',
            forum_like: '❤️',
            zodiac: '⭐',
            holiday: '🎉',
            loyalty: '🎖️',
            level_up: '🏆',
        };

        return icons[type] || '🔔';
    }, []);

    // Obtenir l'icône spécifique selon le type de fête
    const getHolidayIcon = useCallback((notification) => {
        if (notification?.type !== 'holiday' || !notification.holidayName) {
            return null;
        }

        const holidayIcons = {
            "le Noël": "🎄",
            "l'Halloween": "🎃",
            "la Saint-Valentin": "💕",
            "la Pâques": "🐰",
            "le Nouvel An": "🎊",
            "la Fête du Canada": "🍁",
            "la Fête Nationale (France)": "🇫🇷",
            "la Fête Nationale (États-Unis)": "🇺🇸",
            "la Saint-Jean-Baptiste": "⚜️"
        };

        return holidayIcons[notification.holidayName] || "🎉";
    }, []);

    // Obtenir l'image du signe zodiacal
    const getZodiacImage = useCallback((notification) => {
        if (notification?.type === 'zodiac' && notification.zodiacSign && zodiacIcons[notification.zodiacSign]) {
            return zodiacIcons[notification.zodiacSign];
        }
        return null;
    }, []);

    // Navigation vers la section zodiac
    const navigateToZodiacSection = useCallback(() => {
        onClose();
        navigateHomeToSection();
    }, [navigateHomeToSection, onClose]);

    // Navigation vers la section des fêtes (section-3_default aussi)
    const navigateToHolidaySection = useCallback(() => {
        onClose();
        navigateHomeToSection();
    }, [navigateHomeToSection, onClose]);

    // Rendre le bouton d'action pour les notifications d'appels
    const renderCallActionButton = useCallback((notification) => {
        if (!['call_missed', 'call_callback'].includes(notification.type)) {
            return null;
        }

        return (
            <button
                className="call-action-button"
                onClick={(event) => handleVideoCallFromNotification(notification, event)}
                title={t('notifications.callback')}
            >
                📹
            </button>
        );
    }, [handleVideoCallFromNotification]);

    if (!isLoggedIn) {
        return (
            <div className={`notifications-panel ${isOpen ? 'open' : ''}`}>
                <div className="notifications-header">
                    <h3>{t('notifications.title')}</h3>
                    <button onClick={onClose} className="close-notifications" title={t('common.close')}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                <div className="notifications-content">
                    <div className="login-required">
                        <div className="login-icon">🔐</div>
                        <h4>{t('notifications.loginRequired')}</h4>
                        <p>{t('notifications.loginRequiredText')}</p>
                        <button className="login-button" onClick={handleRedirectToLogin}>
                            {t('notifications.login')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <ActivityGainToast
                visible={Boolean(activityGainToast)}
                toastId={activityGainToast?.id}
                points={activityGainToast?.points}
                unitLabel={activityGainToast?.unitLabel}
                label={activityGainToast?.label}
                variant={activityGainToast?.variant}
                onDone={() => setActivityGainToast(null)}
            />
            <div className={`notifications-panel ${isOpen ? 'open' : ''}`}>
                <div className="notifications-header">
                    <div className="header-title">
                        <h3>{t('notifications.title')}</h3>
                        {unreadCount > 0 && (
                            <span className="unread-badge">{unreadCount}</span>
                        )}
                    </div>
                    <div className="notifications-actions">
                        {notifications.length > 0 && (
                            <button
                                onClick={handleClearAllNotifications}
                                className="clear-all-button"
                                disabled={deleting}
                                title={t('notifications.clearAll')}
                            >
                                🗑️
                            </button>
                        )}
                        <button onClick={onClose} className="close-notifications" title={t('common.close')}>
                            ✕
                        </button>
                    </div>
                </div>

                <div className="notifications-content">
                    {error && (
                        <div className="error-banner">
                            <span>{error}</span>
                            <button onClick={() => setError(null)} title={t('common.close')}>✕</button>
                        </div>
                    )}

                    {loading && notifications.length === 0 ? (
                        <div className="notifications-loading">
                            <div className="loading-spinner"></div>
                            <span>{t('notifications.loading')}</span>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="no-notifications">
                            <div className="empty-icon">🔔</div>
                            <h4>{t('notifications.noNotificationsTitle')}</h4>
                            <p>{t('notifications.noNotificationsText')}</p>
                        </div>
                    ) : (
                        <>
                            <ul className="notifications-list">
                                {notifications.map(notification => (
                                    <li
                                        key={notification.id}
                                        className={`${getNotificationClass(notification.type)} ${notification.read ? 'read' : 'unread'}`}
                                        onClick={(event) => handleNotificationClick(notification, event)}
                                    >
                                        <div className="notification-icon">
                                            {notification.type === 'zodiac' && getZodiacImage(notification) ? (
                                                <img
                                                    src={getZodiacImage(notification)}
                                                    alt={t('notifications.zodiacSign')}
                                                    className="zodiac-icon"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        await claimSeasonalBonus(notification);
                                                        navigateToZodiacSection();
                                                    }}
                                                />
                                            ) : notification.type === 'holiday' ? (
                                                <span
                                                    className="icon-emoji holiday-icon"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        await claimSeasonalBonus(notification);
                                                        navigateToHolidaySection();
                                                    }}
                                                    title={t('notifications.viewMoviesFor', { holidayName: notification.holidayName })}
                                                >
                                                    {getHolidayIcon(notification)}
                                                </span>
                                            ) : (
                                                <span
                                                    className={`icon-emoji${notification.type === "call_missed" ? " call-missed-icon" : ""}`}
                                                    aria-label="Appel manqué"
                                                >
                                                    {notification.type === "call_missed" ? (
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                                                            <circle cx="12" cy="12" r="12" fill="url(#red-gradient)" />
                                                            <path d="M6 8c0 4 4 8 8 8l2-2-2-2c-2 2-6-2-6-4l2-2-2-2-2 2z" fill="white" />
                                                            <defs>
                                                                <linearGradient id="red-gradient" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                                                                    <stop stop-color="#c21807" />
                                                                    <stop offset="1" stop-color="#a50000" />
                                                                </linearGradient>
                                                            </defs>
                                                        </svg>
                                                    ) : (
                                                        getNotificationIcon(notification.type, notification)
                                                    )}
                                                </span>
                                            )}

                                            {/* Avatar de l'utilisateur pour les notifications de type communication */}
                                            {['message', 'call', 'call_missed', 'call_ended', 'call_incoming', 'call_callback'].includes(notification.type) && notification.fromAvatar && (
                                                <img
                                                    src={notification.fromAvatar}
                                                    alt={notification.fromUsername}
                                                    className="user-avatar-overlay"
                                                />
                                            )}
                                        </div>

                                        <div className="notification-content">
                                            <div className="notification-message">
                                                {notification.message && notification.message.includes("<strong") ? (
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html: notification.message
                                                        }}
                                                    />
                                                ) : (
                                                    notification.message
                                                )}
                                            </div>
                                            <div className="notification-time">
                                                {formatDate(notification.createdAt)}
                                            </div>
                                        </div>

                                        <div className="notification-actions">
                                            {renderCallActionButton(notification)}

                                            <button
                                                className="delete-notification-button"
                                                onClick={(event) => handleDeleteNotification(event, notification.id)}
                                                title={t('common.delete')}
                                                disabled={deleting}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            {hasMore && (
                                <div className="load-more-container">
                                    <button
                                        className="load-more-button"
                                        onClick={handleLoadMore}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <div className="loading-spinner small"></div>
                                                {t('notifications.loading')}
                                            </>
                                        ) : (
                                            t('notifications.loadMore')
                                        )}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Interface d'appel vidéo depuis les notifications */}
            {showVideoCall && videoCallTarget && (
                <VideoCallManager
                    isOpen={showVideoCall}
                    onClose={handleCloseVideoCall}
                    targetUser={videoCallTarget}
                    mode="outgoing"
                />
            )}
        </>
    );
};

export default NotificationsPanel; 
